import { supabase } from '../lib/supabase';
import {
  User,
  Company,
  Appointment,
  Service,
  Venue,
  AddOn,
  Customer,
  QuestionnaireSubmission,
  SystemConfig,
  MedicalCertificate,
  UserGroup,
  QuestionnaireTemplate,
  CompanyUser,
  OpayoPaymentRequest,
  OpayoPaymentResponse,
  OpayoSessionKeyResponse,
  CheckoutQuoteRequest,
  CheckoutQuoteResponse,
  Payment,
  BookingAvailabilityResponse,
  ManualReminderResponse,
  ManualReminderType,
  PaginatedResponse,
  ActiveSession,
  CommunicationLog,
  CompanyCustomerSummary,
  AuditLog,
  VerifiedCertificate,
  CertificateVerificationResult
} from '../types';
import { normalizeDateOnly } from '../lib/dates';
import { buildQueryString, isUnauthorizedError, toCamelCase } from '../lib/apiHelpers';
import { type Provider } from '@supabase/supabase-js';

export type LoginResult =
  | { error: string; user?: never; company?: never; customer?: never }
  | { user: User; error?: never; company?: never; customer?: never }
  | { company: Company; error?: never; user?: never; customer?: never }
  | { customer: Customer; error?: never; user?: never; company?: never };

const API_BASE = import.meta.env.VITE_API_URL
  ? import.meta.env.VITE_API_URL.replace(/\/$/, '')
  : '';

const AUTH_ERROR_EVENT = 'k2_toast';
let hasShownForbiddenToast = false;
let isHandlingUnauthorizedSignOut = false;
const VALID_BOOKING_STATUSES = new Set(['booked', 'completed', 'cancelled', 'noshow']);
const VALID_QUESTIONNAIRE_STATUSES = new Set(['not_required', 'pending', 'submitted']);

interface ApiValidationIssue {
  path?: string;
  message?: string;
  type?: string;
}

interface ApiErrorBody {
  error?: string;
  issues?: ApiValidationIssue[];
  details?: {
    issues?: ApiValidationIssue[];
  } | null;
}

const clearSuppressSignOut = () => {
  try {
    localStorage.removeItem('k2_suppress_signed_out');
  } catch (_err) {
    // ignore storage errors
  }
};

const emitToast = (message: string) => {
  if (typeof window === 'undefined') return;
  if (hasShownForbiddenToast) return;
  hasShownForbiddenToast = true;
  window.dispatchEvent(
    new CustomEvent(AUTH_ERROR_EVENT, {
      detail: { message, type: 'error', durationMs: 5000 }
    })
  );
};

const handleForbidden = (message?: string) => {
  clearSuppressSignOut();
  emitToast(message || 'Access denied. Please sign in again.');
};

const getAuthHeader = async (accessToken?: string | null) => {
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
    return headers;
  }

  const {
    data: { session }
  } = await supabase.auth.getSession();
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`;
  }
  return headers;
};

const handleUnauthorizedSignOut = async () => {
  if (isHandlingUnauthorizedSignOut) return;
  isHandlingUnauthorizedSignOut = true;
  try {
    markSuppressSignOut();
    await supabase.auth.signOut();
  } finally {
    isHandlingUnauthorizedSignOut = false;
  }
};

const toTrimmedString = (value: unknown) => String(value ?? '').trim();

const toNullableString = (value: unknown): string | null => {
  const normalized = toTrimmedString(value);
  return normalized || null;
};

const isDateLike = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isNaN(new Date(value).getTime());

const isTimeLike = (value: string) => /^\d{2}:\d{2}(?::\d{2})?$/.test(value);

const normalizeTimeLike = (value: unknown) => {
  const normalized = toTrimmedString(value);
  const match = normalized.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return normalized;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = match[3];
  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return normalized;
  }

  const hh = String(hours).padStart(2, '0');
  return seconds ? `${hh}:${match[2]}:${seconds}` : `${hh}:${match[2]}`;
};

const normalizeIsoDateTime = (value: unknown) => {
  const normalized = toTrimmedString(value);
  if (!normalized) return new Date().toISOString();
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return new Date().toISOString();
  return parsed.toISOString();
};

const getValidationIssues = (data: unknown): ApiValidationIssue[] => {
  if (!data || typeof data !== 'object') return [];
  const payload = data as ApiErrorBody;
  if (Array.isArray(payload.issues)) return payload.issues;
  if (Array.isArray(payload.details?.issues)) return payload.details?.issues || [];
  return [];
};

const buildApiErrorMessage = (data: unknown, fallback: string) => {
  const payload = data && typeof data === 'object' ? (data as ApiErrorBody) : null;
  const baseMessage = toTrimmedString(payload?.error) || fallback;
  const firstIssue = getValidationIssues(payload).find(
    (issue) => typeof issue?.message === 'string' && issue.message.trim()
  );
  if (!firstIssue?.message) return baseMessage;

  const issuePath = toTrimmedString(firstIssue.path);
  const issueMessage = firstIssue.message.trim();
  return issuePath
    ? `${baseMessage} (${issuePath}: ${issueMessage})`
    : `${baseMessage} (${issueMessage})`;
};

const buildCreateBookingPayload = (booking: Appointment) => {
  const id = toTrimmedString(booking.id);
  const venueId = toTrimmedString(booking.venueId);
  const serviceId = toTrimmedString(booking.serviceId);
  const customerName = toTrimmedString(booking.customerName);
  const customerEmail = toTrimmedString(booking.customerEmail);
  const normalizedDate = normalizeDateOnly(booking.date);
  const normalizedTime = normalizeTimeLike(booking.time);
  const normalizedCompanyId = toNullableString(booking.companyId);
  const addOnIds = Array.isArray(booking.addOnIds)
    ? booking.addOnIds
        .map((value) => toTrimmedString(value))
        .filter((value): value is string => Boolean(value))
    : [];
  const paymentMethod =
    booking.paymentMethod === 'card' || booking.paymentMethod === 'invoice'
      ? booking.paymentMethod
      : normalizedCompanyId
        ? 'invoice'
        : 'card';

  if (!id) throw new Error('Booking reference is required.');
  if (!venueId) throw new Error('Venue is required to create a booking.');
  if (!serviceId) throw new Error('Service is required to create a booking.');
  if (!customerName) throw new Error('Customer name is required to create a booking.');
  if (!customerEmail) throw new Error('Customer email is required to create a booking.');
  if (!normalizedDate) throw new Error('Booking date is required.');
  if (!normalizedTime) throw new Error('Booking time is required.');
  if (!isDateLike(normalizedDate)) {
    throw new Error('Booking date is invalid. Use YYYY-MM-DD.');
  }
  if (!isTimeLike(normalizedTime)) {
    throw new Error('Booking time is invalid. Use HH:mm.');
  }

  return {
    id,
    venueId,
    serviceId,
    addOnIds,
    date: normalizedDate,
    time: normalizedTime,
    customerName,
    customerEmail,
    customerPhone: toNullableString(booking.customerPhone),
    customerDob: toNullableString(booking.customerDob),
    customerJobTitle: toNullableString(booking.customerJobTitle),
    companyId: normalizedCompanyId,
    poNumber: toNullableString(booking.poNumber),
    paymentMethod,
    paymentId: toNullableString(booking.paymentId),
    questionnaireStatus: VALID_QUESTIONNAIRE_STATUSES.has(booking.questionnaireStatus)
      ? booking.questionnaireStatus
      : 'not_required',
    allowOverbook: booking.allowOverbook === true ? true : undefined,
    status: VALID_BOOKING_STATUSES.has(booking.status) ? booking.status : 'booked',
    questionnaireSubmissionId: toNullableString(booking.questionnaireSubmissionId),
    certificateId: toNullableString(booking.certificateId),
    createdAt: normalizeIsoDateTime(booking.createdAt)
  };
};

const apiFetch = async (path: string, options: RequestInit = {}, accessToken?: string | null) => {
  const authHeader = await getAuthHeader(accessToken);
  const response = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...authHeader, ...(options.headers || {}) },
    ...options
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 401) {
      await handleUnauthorizedSignOut();
    }
    if (response.status === 403) {
      handleForbidden(buildApiErrorMessage(data, response.statusText));
    }
    throw new Error(buildApiErrorMessage(data, response.statusText));
  }
  return data;
};

const apiFetchPublic = async (path: string, options: RequestInit = {}) => {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 403) {
      handleForbidden(buildApiErrorMessage(data, response.statusText));
    }
    throw new Error(buildApiErrorMessage(data, response.statusText));
  }
  return data;
};

type ListQueryParams = Record<string, string | number | boolean | undefined>;

const buildListPath = (path: string, params: ListQueryParams) => {
  const query = buildQueryString(params);
  return query ? `${path}?${query}` : path;
};

const fetchPaginatedList = async <T>(
  path: string,
  params: ListQueryParams
): Promise<PaginatedResponse<T>> => {
  const data = await apiFetch(buildListPath(path, params));
  return {
    items: (toCamelCase(data.items || []) as T[]) || [],
    totalCount: Number(data.totalCount || 0),
    page: Number(data.page || 1),
    pageSize: Number(data.pageSize || 0)
  };
};

const fetchList = async <T>(path: string, params: ListQueryParams): Promise<T[]> => {
  const data = await apiFetch(buildListPath(path, params));
  return (toCamelCase(data || []) as T[]) || [];
};

const toCamelArray = <T>(value: unknown): T[] => {
  if (!Array.isArray(value)) return [];
  return toCamelCase(value) as T[];
};

interface PublicReferenceData {
  venues: Venue[];
  services: Service[];
  addOns: AddOn[];
}

interface InitialData {
  appointments: Appointment[];
  services: Service[];
  venues: Venue[];
  addOns: AddOn[];
  users: User[];
  companies: Company[];
  customers: Customer[];
  userGroups: UserGroup[];
  submissions: QuestionnaireSubmission[];
  certificates: MedicalCertificate[];
  payments: Payment[];
  systemConfig: SystemConfig;
  questionnaires: QuestionnaireTemplate[];
  errors: Record<string, string>;
}

const markSuppressSignOut = () => {
  try {
    localStorage.setItem('k2_suppress_signed_out', '1');
  } catch (_err) {
    // ignore storage errors
  }
};

export const api = {
  fetchInitialData: async (): Promise<InitialData> => {
    const {
      data: { session }
    } = await supabase.auth.getSession();

    let endpoint = '/api/public/init';
    let usedSession = false;
    if (session) {
      const userType = session.user.user_metadata?.user_type || 'staff';
      if (userType === 'staff') endpoint = '/api/init';
      else if (userType === 'company') endpoint = '/api/company/init';
      else if (userType === 'customer') endpoint = '/api/customer/init';
      usedSession = true;
    }

    let data;
    try {
      data = await apiFetch(endpoint);
    } catch (error) {
      if (usedSession && isUnauthorizedError(error)) {
        markSuppressSignOut();
        await supabase.auth.signOut();
        data = await apiFetchPublic('/api/public/init');
      } else {
        throw error;
      }
    }
    if (!data?.systemConfig) {
      throw new Error('System configuration is missing.');
    }

    const dbConfig = toCamelCase(data.systemConfig);

    return {
      appointments: toCamelArray<Appointment>(data.appointments || []),
      services: toCamelArray<Service>(data.services || []),
      venues: toCamelArray<Venue>(data.venues || []),
      addOns: toCamelArray<AddOn>(data.addOns || []),
      users: toCamelArray<User>(data.users || []),
      companies: toCamelArray<Company>(data.companies || []),
      customers: toCamelArray<Customer>(data.customers || []),
      userGroups: toCamelArray<UserGroup>(data.userGroups || []),
      submissions: toCamelArray<QuestionnaireSubmission>(data.submissions || []),
      certificates: toCamelArray<MedicalCertificate>(data.certificates || []),
      payments: toCamelArray<Payment>(data.payments || []),
      systemConfig: dbConfig as SystemConfig,
      questionnaires: toCamelArray<QuestionnaireTemplate>(data.questionnaires || []),
      errors: data.errors || {}
    };
  },

  fetchPublicReference: async (): Promise<PublicReferenceData> => {
    const data = await apiFetchPublic('/api/public/reference');
    return {
      venues: toCamelArray<Venue>(data.venues || []),
      services: toCamelArray<Service>(data.services || []),
      addOns: toCamelArray<AddOn>(data.addOns || [])
    };
  },

  fetchCompanyReference: async (): Promise<PublicReferenceData> => {
    const data = await apiFetch('/api/company/reference');
    return {
      venues: toCamelArray<Venue>(data.venues || []),
      services: toCamelArray<Service>(data.services || []),
      addOns: toCamelArray<AddOn>(data.addOns || [])
    };
  },

  fetchCustomerReference: async (): Promise<PublicReferenceData> => {
    const data = await apiFetch('/api/customer/reference');
    return {
      venues: toCamelArray<Venue>(data.venues || []),
      services: toCamelArray<Service>(data.services || []),
      addOns: toCamelArray<AddOn>(data.addOns || [])
    };
  },

  getSystemConfig: async (): Promise<SystemConfig> => {
    const data = await apiFetch('/api/config');
    return toCamelCase(data?.systemConfig || {}) as SystemConfig;
  },

  getPublicConfig: async (): Promise<SystemConfig> => {
    const data = await apiFetchPublic('/api/config/public');
    return toCamelCase(data?.systemConfig || {}) as SystemConfig;
  },

  listAuditLogs: (params: ListQueryParams): Promise<PaginatedResponse<AuditLog>> =>
    fetchPaginatedList('/api/audit-logs', params),
  listStaffServices: (params: ListQueryParams): Promise<PaginatedResponse<Service>> =>
    fetchPaginatedList('/api/staff/services', params),
  listStaffAddOns: (params: ListQueryParams): Promise<PaginatedResponse<AddOn>> =>
    fetchPaginatedList('/api/staff/addons', params),
  listStaffVenues: (params: ListQueryParams): Promise<PaginatedResponse<Venue>> =>
    fetchPaginatedList('/api/staff/venues', params),
  listStaffQuestionnaires: (
    params: ListQueryParams
  ): Promise<PaginatedResponse<QuestionnaireTemplate>> =>
    fetchPaginatedList('/api/staff/questionnaires', params),
  listStaffUserGroups: (params: ListQueryParams): Promise<PaginatedResponse<UserGroup>> =>
    fetchPaginatedList('/api/staff/user-groups', params),
  listStaffUsers: (params: ListQueryParams): Promise<PaginatedResponse<User>> =>
    fetchPaginatedList('/api/staff/users', params),
  listStaffCustomers: (params: ListQueryParams): Promise<PaginatedResponse<Customer>> =>
    fetchPaginatedList('/api/staff/customers', params),
  listStaffCustomerHistory: (
    customerId: string,
    params: ListQueryParams
  ): Promise<PaginatedResponse<Appointment>> =>
    fetchPaginatedList(`/api/staff/customers/${customerId}/history`, params),
  listStaffCustomerLogs: (
    customerId: string,
    params: ListQueryParams
  ): Promise<PaginatedResponse<CommunicationLog>> =>
    fetchPaginatedList(`/api/staff/customers/${customerId}/logs`, params),
  listStaffCompanies: (params: ListQueryParams): Promise<PaginatedResponse<Company>> =>
    fetchPaginatedList('/api/staff/companies', params),
  listCompanyTeam: (
    companyId: string,
    params: ListQueryParams
  ): Promise<PaginatedResponse<CompanyUser>> =>
    fetchPaginatedList(`/api/staff/companies/${companyId}/team`, params),
  listCompanyCustomers: (
    companyId: string,
    params: ListQueryParams
  ): Promise<PaginatedResponse<CompanyCustomerSummary>> =>
    fetchPaginatedList(`/api/staff/companies/${companyId}/customers`, params),
  listStaffSessions: (params: ListQueryParams): Promise<ActiveSession[]> =>
    fetchList('/api/staff/sessions', params),

  revokeStaffSession: async (id: string, reason?: string): Promise<{ success: boolean }> => {
    await apiFetch(`/api/staff/sessions/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify({
        reason: reason || null
      })
    });
    return { success: true };
  },

  revokeCurrentSession: async (reason?: string): Promise<{ success: boolean }> => {
    await apiFetch('/api/session/revoke', {
      method: 'POST',
      body: JSON.stringify({
        reason: reason || null
      })
    });
    return { success: true };
  },

  listStaffBookings: (params: ListQueryParams): Promise<PaginatedResponse<Appointment>> =>
    fetchPaginatedList('/api/staff/bookings', params),
  listCompanyBookings: (params: ListQueryParams): Promise<PaginatedResponse<Appointment>> =>
    fetchPaginatedList('/api/company/bookings', params),
  listCustomerBookings: (params: ListQueryParams): Promise<PaginatedResponse<Appointment>> =>
    fetchPaginatedList('/api/customer/bookings', params),
  listStaffPayments: (params: ListQueryParams): Promise<PaginatedResponse<Payment>> =>
    fetchPaginatedList('/api/staff/payments', params),
  listStaffCertificates: (
    params: ListQueryParams
  ): Promise<PaginatedResponse<MedicalCertificate>> =>
    fetchPaginatedList('/api/staff/certificates', params),
  listCompanyCertificates: (
    params: ListQueryParams
  ): Promise<PaginatedResponse<MedicalCertificate>> =>
    fetchPaginatedList('/api/company/certificates', params),
  listCustomerCertificates: (
    params: ListQueryParams
  ): Promise<PaginatedResponse<MedicalCertificate>> =>
    fetchPaginatedList('/api/customer/certificates', params),

  verifyCertificatePublic: async (
    certificateId: string,
    name?: string
  ): Promise<CertificateVerificationResult> => {
    const id = String(certificateId || '').trim();
    if (!id) {
      throw new Error('Certificate ID is required');
    }
    const nameText = String(name || '').trim();
    const suffix = buildQueryString({ name: nameText || undefined });
    const querySuffix = suffix ? `?${suffix}` : '';
    const data = await apiFetchPublic(
      `/api/certificates/verify/${encodeURIComponent(id)}${querySuffix}`
    );
    return {
      certificate: toCamelCase(data.certificate || {}) as VerifiedCertificate,
      requiresNameConfirmation: Boolean(data.requiresNameConfirmation),
      nameConfirmed: Boolean(data.nameConfirmed)
    };
  },

  listStaffSubmissions: (
    params: ListQueryParams
  ): Promise<PaginatedResponse<QuestionnaireSubmission>> =>
    fetchPaginatedList('/api/staff/submissions', params),
  listCompanySubmissions: (
    params: ListQueryParams
  ): Promise<PaginatedResponse<QuestionnaireSubmission>> =>
    fetchPaginatedList('/api/company/submissions', params),
  listCustomerSubmissions: (
    params: ListQueryParams
  ): Promise<PaginatedResponse<QuestionnaireSubmission>> =>
    fetchPaginatedList('/api/customer/submissions', params),

  getDefaultSystemConfig: async (): Promise<SystemConfig> => {
    const data = await apiFetch('/api/config/defaults');
    return toCamelCase(data || {}) as SystemConfig;
  },

  getCurrentSession: async () => {
    const {
      data: { session }
    } = await supabase.auth.getSession();

    if (!session) return null;

    try {
      const sessionProfile = await apiFetch('/api/session');
      if (sessionProfile?.type === 'company') {
        const status = String(sessionProfile.profile?.status || '').toLowerCase();
        if (['pending', 'on_hold', 'rejected'].includes(status)) {
          markSuppressSignOut();
          await supabase.auth.signOut();
          return null;
        }
      }
      return sessionProfile || null;
    } catch (e) {
      return null;
    }
  },

  login: async (
    email: string,
    pass: string,
    context: 'staff' | 'company' | 'customer'
  ): Promise<LoginResult> => {
    const cleanEmail = email.trim().toLowerCase();

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password: pass
      });

      if (authError) {
        return { error: authError.message || 'Invalid credentials.' };
      }

      const sessionProfile = await apiFetch('/api/session', {}, authData.session?.access_token);
      if (sessionProfile && sessionProfile.type === context) {
        if (sessionProfile.type === 'company') {
          const status = String(sessionProfile.profile?.status || '').toLowerCase();
          if (['pending', 'on_hold', 'rejected'].includes(status)) {
            markSuppressSignOut();
            await supabase.auth.signOut();
            const message =
              status === 'rejected'
                ? 'Your company account was rejected. Please contact support.'
                : status === 'on_hold'
                  ? 'Your company account is on hold. Please contact support.'
                  : 'Your company account is pending approval. Please wait for admin confirmation or contact support.';
            return { error: message };
          }
        }
        return sessionProfile.type === 'staff'
          ? { user: sessionProfile.profile }
          : sessionProfile.type === 'company'
            ? { company: sessionProfile.profile }
            : { customer: sessionProfile.profile };
      }

      if (sessionProfile?.type) {
        markSuppressSignOut();
        await supabase.auth.signOut();

        const contextLabel =
          context === 'staff'
            ? 'Staff Portal'
            : context === 'company'
              ? 'Company Portal'
              : 'Patient Portal';
        return { error: `This account does not belong to ${contextLabel}.` };
      }

      markSuppressSignOut();
      await supabase.auth.signOut();
      return { error: 'Login succeeded but no profile was found for this account.' };
    } catch (e: unknown) {
      return {
        error: e instanceof Error ? e.message : 'An unexpected error occurred during login.'
      };
    }
  },

  loginWithMicrosoft: async () => {
    const provider = String(import.meta.env.VITE_SSO_PROVIDER || 'azure').trim();
    const configuredRedirect = String(import.meta.env.VITE_SSO_REDIRECT_URL || '').trim();
    const baseRedirect =
      configuredRedirect || `${window.location.origin}${window.location.pathname}`;
    const redirectTo = `${baseRedirect}${baseRedirect.includes('?') ? '&' : '?'}sso=1`;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: provider as Provider,
      options: { redirectTo }
    });

    if (error) {
      throw error;
    }
  },

  createStaffUser: async (user: User) => {
    await apiFetch('/api/staff-users', {
      method: 'POST',
      body: JSON.stringify(user)
    });
    return { success: true };
  },

  deleteUser: async (id: string) => {
    await apiFetch(`/api/users/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  resetStaffPassword: async (id: string, password?: string) => {
    await apiFetch(`/api/staff-users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({
        password,
        redirectTo: `${window.location.origin}${window.location.pathname}?mode=reset`
      })
    });
    return { success: true };
  },

  registerCompany: async (company: Company) => {
    await apiFetch('/api/company/register', {
      method: 'POST',
      body: JSON.stringify({
        name: company.name,
        contactPerson: company.contactPerson,
        email: company.email,
        address: company.address,
        phone: company.phone || null
      })
    });
    return { success: true };
  },

  createCompanyWithAuth: async (company: Company) => {
    await apiFetch('/api/companies', {
      method: 'POST',
      body: JSON.stringify({
        name: company.name,
        contactPerson: company.contactPerson,
        email: company.email,
        address: company.address,
        phone: company.phone || null,
        status: company.status,
        tier: company.tier,
        paymentPreference: company.paymentPreference
      })
    });
    return { success: true };
  },

  createCompanyMember: async (companyId: string, member: Partial<CompanyUser>) => {
    await apiFetch('/api/company-users', {
      method: 'POST',
      body: JSON.stringify({
        companyId,
        name: member.name,
        email: member.email,
        receiveNotifications: member.receiveNotifications
      })
    });
    return { success: true };
  },

  updateCompanyMember: async (memberId: string, updates: Partial<CompanyUser>) => {
    await apiFetch(`/api/company-users/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: updates.name,
        email: updates.email,
        receiveNotifications: updates.receiveNotifications
      })
    });
    return { success: true };
  },

  deleteCompanyMember: async (memberId: string) => {
    await apiFetch(`/api/company-users/${memberId}`, { method: 'DELETE' });
    return { success: true };
  },

  resetCompanyMemberPassword: async (memberId: string) => {
    await apiFetch(`/api/company-users/${memberId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({
        redirectTo: `${window.location.origin}${window.location.pathname}?mode=reset`
      })
    });
    return { success: true };
  },

  createCustomerUser: async (customer: Customer) => {
    await apiFetch('/api/customers', {
      method: 'POST',
      body: JSON.stringify(customer)
    });
    return { success: true };
  },

  logout: async () => {
    try {
      await api.revokeCurrentSession('User logout');
    } catch (_err) {
      // Keep local sign-out resilient if session endpoint fails.
    }
    await supabase.auth.signOut();
  },

  uploadFile: async (file: File, bucket: string): Promise<string> => {
    const {
      data: { session }
    } = await supabase.auth.getSession();
    const authHeader: Record<string, string> = {};
    if (session?.access_token) {
      authHeader.Authorization = `Bearer ${session.access_token}`;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bucket', bucket);
    const response = await fetch(`${API_BASE}/api/uploads/logo`, {
      method: 'POST',
      headers: authHeader,
      body: formData
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(data, response.statusText));
    }
    if (!data?.publicUrl) {
      throw new Error('Upload failed.');
    }
    return data.publicUrl;
  },

  createBooking: async (booking: Appointment) => {
    const payload = buildCreateBookingPayload(booking);
    await apiFetch('/api/bookings', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    return { success: true };
  },

  getBookingAvailability: async ({
    venueId,
    date,
    serviceId,
    addOnIds = [],
    excludeAppointmentId
  }: {
    venueId: string;
    date: string | Date;
    serviceId: string;
    addOnIds?: string[];
    excludeAppointmentId?: string;
  }): Promise<BookingAvailabilityResponse> => {
    const normalizedDate = normalizeDateOnly(date);
    const data = await apiFetch('/api/bookings/availability', {
      method: 'POST',
      body: JSON.stringify({
        venueId,
        date: normalizedDate || date,
        serviceId,
        addOnIds,
        excludeAppointmentId
      })
    });
    return toCamelCase(data) as BookingAvailabilityResponse;
  },

  createOpayoSessionKey: async (): Promise<OpayoSessionKeyResponse> => {
    const data = await apiFetch('/api/payments/opayo/session-key', {
      method: 'POST',
      body: JSON.stringify({})
    });
    return data as OpayoSessionKeyResponse;
  },

  processOpayoPayment: async (payload: OpayoPaymentRequest): Promise<OpayoPaymentResponse> => {
    const data = await apiFetch('/api/payments/opayo/charge', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    return data as OpayoPaymentResponse;
  },

  getCheckoutQuote: async (payload: CheckoutQuoteRequest): Promise<CheckoutQuoteResponse> => {
    const data = await apiFetch('/api/payments/quote', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    return data as CheckoutQuoteResponse;
  },

  updateBooking: async (booking: Appointment, notify?: { email: boolean; sms: boolean }) => {
    const normalizedDate = normalizeDateOnly(booking.date);
    await apiFetch(`/api/bookings/${booking.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        ...booking,
        date: normalizedDate || booking.date,
        notify
      })
    });
    return { success: true };
  },

  deleteBooking: async (id: string) => {
    if (!id) throw new Error('Deletion ID is missing.');
    await apiFetch(`/api/bookings/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  triggerReminder: async (
    appointmentId: string,
    type: ManualReminderType = 'both',
    templateType?: string
  ): Promise<ManualReminderResponse> => {
    const data = await apiFetch(`/api/reminders/trigger/${appointmentId}`, {
      method: 'POST',
      body: JSON.stringify(
        templateType
          ? {
              type,
              templateType
            }
          : { type }
      )
    });
    return data as ManualReminderResponse;
  },

  createService: async (service: Service) => {
    await apiFetch('/api/services', { method: 'POST', body: JSON.stringify(service) });
    return { success: true };
  },
  updateService: async (service: Service) => {
    await apiFetch(`/api/services/${service.id}`, { method: 'PUT', body: JSON.stringify(service) });
    return { success: true };
  },
  deleteService: async (id: string) => {
    await apiFetch(`/api/services/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  createVenue: async (venue: Venue) => {
    await apiFetch('/api/venues', { method: 'POST', body: JSON.stringify(venue) });
    return { success: true };
  },
  updateVenue: async (venue: Venue) => {
    await apiFetch(`/api/venues/${venue.id}`, { method: 'PUT', body: JSON.stringify(venue) });
    return { success: true };
  },
  deleteVenue: async (id: string) => {
    await apiFetch(`/api/venues/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  createAddOn: async (addOn: AddOn) => {
    await apiFetch('/api/addons', { method: 'POST', body: JSON.stringify(addOn) });
    return { success: true };
  },
  updateAddOn: async (addOn: AddOn) => {
    await apiFetch(`/api/addons/${addOn.id}`, { method: 'PUT', body: JSON.stringify(addOn) });
    return { success: true };
  },
  deleteAddOn: async (id: string) => {
    await apiFetch(`/api/addons/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  createUserGroup: async (group: UserGroup) => {
    await apiFetch('/api/user-groups', { method: 'POST', body: JSON.stringify(group) });
    return { success: true };
  },
  updateUserGroup: async (group: UserGroup) => {
    await apiFetch(`/api/user-groups/${group.id}`, {
      method: 'PATCH',
      body: JSON.stringify(group)
    });
    return { success: true };
  },
  deleteUserGroup: async (id: string) => {
    await apiFetch(`/api/user-groups/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  createQuestionnaire: async (questionnaire: QuestionnaireTemplate) => {
    await apiFetch('/api/questionnaires', { method: 'POST', body: JSON.stringify(questionnaire) });
    return { success: true };
  },
  updateQuestionnaire: async (questionnaire: QuestionnaireTemplate) => {
    await apiFetch(`/api/questionnaires/${questionnaire.id}`, {
      method: 'PUT',
      body: JSON.stringify(questionnaire)
    });
    return { success: true };
  },
  deleteQuestionnaire: async (id: string) => {
    await apiFetch(`/api/questionnaires/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  createCustomer: async (customer: Customer) => {
    await apiFetch('/api/customers', { method: 'POST', body: JSON.stringify(customer) });
    return { success: true };
  },
  updateCustomer: async (customer: Customer) => {
    await apiFetch(`/api/customers/${customer.id}`, {
      method: 'PATCH',
      body: JSON.stringify(customer)
    });
    return { success: true };
  },
  deleteCustomer: async (id: string) => {
    await apiFetch(`/api/customers/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  updateCompany: async (company: Partial<Company> & { id: string }) => {
    await apiFetch(`/api/companies/${company.id}`, {
      method: 'PATCH',
      body: JSON.stringify(company)
    });
    return { success: true };
  },

  deleteCompany: async (id: string) => {
    await apiFetch(`/api/companies/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  updateSystemConfig: async (config: SystemConfig) => {
    await apiFetch('/api/config', {
      method: 'POST',
      body: JSON.stringify(config)
    });
    return { success: true };
  },

  updateBookingStatus: async (id: string, status: string) => {
    await apiFetch(`/api/bookings/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
    return { success: true };
  },

  updateUserProfile: async (user: User) => {
    await apiFetch(`/api/users/${user.id}`, {
      method: 'PATCH',
      body: JSON.stringify(user)
    });
    return { success: true };
  },

  issueCertificate: async (cert: MedicalCertificate) => {
    await apiFetch('/api/certificates', {
      method: 'POST',
      body: JSON.stringify(cert)
    });
    return { success: true };
  },

  submitQuestionnaire: async (submission: QuestionnaireSubmission) => {
    await apiFetch('/api/submissions', {
      method: 'POST',
      body: JSON.stringify(submission)
    });
    return { success: true };
  },

  updateCompanyPassword: async (companyId: string, memberId: string, pass: string) => {
    const { error: authError } = await supabase.auth.updateUser({ password: pass });
    if (authError) throw authError;

    if (memberId !== 'primary') {
      await apiFetch(`/api/company-users/${memberId}/password-updated`, { method: 'POST' });
    }
    return { success: true };
  },

  updateCustomerPassword: async (customerId: string, pass: string) => {
    const { error: authError } = await supabase.auth.updateUser({ password: pass });
    if (authError) throw authError;

    await apiFetch(`/api/customers/${customerId}/password-updated`, { method: 'POST' });
    return { success: true };
  },

  requestPasswordReset: async (email: string, redirectTo?: string) => {
    await apiFetch('/api/auth/password-reset', {
      method: 'POST',
      body: JSON.stringify({
        email,
        redirectTo: redirectTo || `${window.location.origin}${window.location.pathname}?mode=reset`
      })
    });
    return { success: true };
  },

  requestCustomerLoginLink: async (email: string, redirectTo?: string) => {
    const data = await apiFetch('/api/auth/customer-login-link', {
      method: 'POST',
      body: JSON.stringify({
        email,
        redirectTo: redirectTo || `${window.location.origin}${window.location.pathname}`
      })
    });
    return data || { success: true };
  }
};
